import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import ErrorMessage from '../ui/ErrorMessage';
import mcqService from '../../services/mcqService';

/**
 * MCQFormModal - Modal for creating/editing MCQ sets
 * Handles MCQ metadata (title, description, is_preview, order)
 * Questions are managed separately in ManageQuestions page
 */
const MCQFormModal = ({ isOpen, onClose, onSave, mcq, courseId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_preview: false
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Initialize form data when editing
  useEffect(() => {
    if (mcq && isOpen) {
      setFormData({
        title: mcq.title || '',
        description: mcq.description || '',
        is_preview: mcq.is_preview || false
      });
    }
  }, [mcq, isOpen]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      is_preview: false
    });
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      setError('MCQ title is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (mcq) {
        // Update existing MCQ
        await mcqService.updateMCQ(mcq.id, formData);
      } else {
        // Create new MCQ
        await mcqService.createMCQ({
          ...formData,
          course_id: courseId
        });
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to save MCQ:', err);
      setError(err.response?.data?.detail || err.response?.data?.title?.[0] || 'Failed to save MCQ. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mcq ? 'Edit MCQ' : 'Create New MCQ'}
      maxWidth="2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <ErrorMessage message={error} onClose={() => setError('')} />
        )}

        {/* MCQ Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            MCQ Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="e.g., Chapter 1 Assessment, Final Exam MCQs"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description (Optional)
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="3"
            placeholder="Brief description of this MCQ set..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-sm text-gray-500 mt-1">
            MCQ sets are automatically ordered in the sequence they are created.
          </p>
        </div>

        {/* Preview Checkbox */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_preview"
            name="is_preview"
            checked={formData.is_preview}
            onChange={handleInputChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="is_preview" className="ml-2 block text-sm text-gray-900">
            Free Preview MCQ
          </label>
        </div>
        <p className="text-sm text-gray-500 ml-6 -mt-4">
          If checked, all users can access this MCQ. Otherwise, only enrolled users can access it.
        </p>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Next Step: Add Questions</p>
              <p>After creating this MCQ, you'll be able to add questions with 4 options each from the MCQ management page.</p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'Saving...' : (mcq ? 'Update MCQ' : 'Create MCQ')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default MCQFormModal;
